import { saveBlob } from "~/utils/downloadBlob";
import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  AddTreatmentJournalPayload,
  BodyPartDiagnose,
  ChangeTreatmentParametersPayload,
  Checkpoint,
  CheckpointIdentifier,
  CollectionResponse,
  CreateTreatmentDocumentPayload,
  EmptyResponse,
  FilterByQuery,
  IncludeQuery,
  IntakeIdentifier,
  LetterTemplate,
  PaginationQuery,
  PatientIdentifier,
  RecentTreatmentEpisode,
  Response,
  SaveTreatmentJournalDraftPayload,
  SendTreatmentResponse,
  SortByQuery,
  StartTreatmentPayload,
  StopTreatmentPayload,
  StopTreatmentResponse,
  StoredFile,
  StoreTreatmentPayload,
  StoreTreatmentResponse,
  SubmitCheckpointPayload,
  Treatment,
  TreatmentAppointmentOverview,
  TreatmentChangeFlow,
  TreatmentCreditRequest,
  TreatmentDocument,
  TreatmentDocumentIdentifier,
  TreatmentIdentifier,
  TreatmentJournal,
  TreatmentJournalCopySource,
  TreatmentJournalIdentifier,
  UpdateTreatmentPayload,
} from "~/types/api";

export function createTreatmentApi({
  getRequest,
  postRequest,
  putRequest,
  patchRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Starts a new treatment from an existing intake. */
  const startTreatment = (intakeId: IntakeIdentifier, data: StartTreatmentPayload) =>
    postRequest<Response<Treatment>>(`/intakes/${intakeId}/start-treatment`, data);

  /** Updates an existing treatment's details. */
  const updateTreatment = (treatmentId: TreatmentIdentifier, data: UpdateTreatmentPayload) =>
    postRequest<Response<Treatment>>(`/treatments/${treatmentId}`, data);

  /** Updates the referral information on a treatment. */
  const updateTreatmentReferral = (
    treatmentId: TreatmentIdentifier,
    data: {
      has_referral: boolean;
      referrer_id: string | null;
      referred_date: string | null;
      authorization_number?: string | null;
      authorization_date?: string | null;
      care_eligibility_date?: string | null;
    },
  ) => patchRequest<EmptyResponse>(`/treatments/${treatmentId}/referral`, data);

  const recentTreatmentEpisodes = (patientId: PatientIdentifier) =>
    getRequest<CollectionResponse<RecentTreatmentEpisode>>(`/patients/${patientId}/treatments/recent-episodes`);

  /** Retrieves the change-parameters flow for a treatment. */
  const getTreatmentChangeFlow = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<TreatmentChangeFlow>>(`/treatments/${treatmentId}/change-parameters-flow`);

  /** Changes treatment parameters such as insurance or product codes. */
  const changeTreatmentParameters = (treatmentId: TreatmentIdentifier, data: ChangeTreatmentParametersPayload) =>
    postRequest<Response<Treatment | TreatmentCreditRequest>>(`/treatments/${treatmentId}/change-parameters`, data);

  /** Fetches the pending credit request for a treatment. */
  const getTreatmentCreditRequest = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<TreatmentCreditRequest | null>>(`/treatments/${treatmentId}/credit-request`);

  /** Updates body part diagnoses for a treatment. */
  const updateBodyPartDiagnoses = (
    treatmentId: TreatmentIdentifier,
    data: { appointment_id?: number; body_parts_diagnoses: BodyPartDiagnose[] },
  ) => postRequest<Response<Treatment>>(`/treatments/${treatmentId}/body-parts-diagnoses`, data);

  /** Fetches body part diagnoses for a treatment, optionally filtered by appointment. */
  const getBodyPartDiagnoses = (treatmentId: TreatmentIdentifier, appointmentId?: number) =>
    getRequest<Response<BodyPartDiagnose[]>>(`/treatments/${treatmentId}/body-parts-diagnoses`, {
      params: {
        appointment_id: appointmentId,
      },
    });

  /** Stops (closes) an active treatment. */
  const stopTreatment = (treatmentId: TreatmentIdentifier, data: StopTreatmentPayload) =>
    postRequest<StopTreatmentResponse>(`/treatments/${treatmentId}/stop`, data);

  /** Deletes a treatment permanently. */
  const deleteTreatment = (treatmentId: TreatmentIdentifier) =>
    deleteRequest<Response<void>>(`/treatments/${treatmentId}`);

  /** Fetches a single treatment by ID. */
  const treatment = (treatmentId: TreatmentIdentifier) => getRequest<Response<Treatment>>(`/treatments/${treatmentId}`);

  /** Fetches all treatments for a patient. */
  const treatments = (patientId: PatientIdentifier) =>
    getRequest<Response<Treatment>>(`patients/${patientId}/treatments`);

  /**
   * Creates a new treatment for a patient. Without `location_id` the backend uses the patient's
   * primary location, or the current location when the patient has none.
   */
  const storeTreatment = (patientId: PatientIdentifier, data: StoreTreatmentPayload = {}) =>
    postRequest<StoreTreatmentResponse>(`/patients/${patientId}/treatment`, data);

  /**
   * Sends the intake request mail for a patient. `intakeId` targets a specific intake — omit it
   * to fall back to the patient's single most-recently created open intake (e.g. right after
   * creating one).
   */
  const sendTreatment = (patientId: PatientIdentifier, intakeId?: IntakeIdentifier) =>
    postRequest<SendTreatmentResponse>(`/patients/${patientId}/treatment/send`, { intake_id: intakeId });

  /** Fetches the available stop reasons for a treatment. */
  const treatmentStopReasons = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<Record<string, string>>>(`/treatments/${treatmentId}/stop-reasons`);

  /** Fetches the list of referrer provider types. */
  const referrerProviderTypes = () =>
    getRequest<Response<{ value: string; label: string }[]>>(`/treatments/referrer-provider-types`);

  /** Fetches all journals for a treatment. */
  const treatmentJournals = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<TreatmentJournal[]>>(`/treatments/${treatmentId}/journals`);

  /** Fetches a paginated list of all treatment journals across treatments. */
  const fetchAllTreatmentJournals = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<TreatmentJournal>("/treatment-journals", pagination, sortBy, includes, filterBy);

  /** Fetches the appointments overview for a treatment. */
  const treatmentAppointmentsOverview = (treatmentId: TreatmentIdentifier) =>
    getRequest<CollectionResponse<TreatmentAppointmentOverview>>(`/treatments/${treatmentId}/appointments-overview`);

  /** Fetches a single treatment journal entry. */
  const treatmentJournal = (treatmentId: TreatmentIdentifier, treatmentJournalId: TreatmentJournalIdentifier) =>
    getRequest<Response<TreatmentJournal>>(`/treatments/${treatmentId}/journals/${treatmentJournalId}`);

  /** Fetches the copyable fields from a treatment journal in the same treatment. */
  const treatmentJournalCopySource = (
    treatmentId: TreatmentIdentifier,
    treatmentJournalId: TreatmentJournalIdentifier,
  ) =>
    getRequest<Response<TreatmentJournalCopySource>>(
      `/treatments/${treatmentId}/journals/${treatmentJournalId}/copy-source`,
    );

  /** Adds a new journal entry to a treatment. */
  const addTreatmentJournal = (treatmentId: TreatmentIdentifier, data: AddTreatmentJournalPayload) =>
    postRequest<Response<TreatmentJournal>>(`/treatments/${treatmentId}/journals`, data);

  /** Saves a draft journal entry for a treatment. */
  const saveTreatmentJournalDraft = (treatmentId: TreatmentIdentifier, data: SaveTreatmentJournalDraftPayload) =>
    postRequest<Response<TreatmentJournal>>(`/treatments/${treatmentId}/journals/draft`, data);

  /** Fetches all documents attached to a treatment. */
  const treatmentDocuments = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<TreatmentDocument[]>>(`/treatments/${treatmentId}/documents`);

  const treatmentReferralAttachments = (treatmentId: TreatmentIdentifier) =>
    getRequest<Response<StoredFile[]>>(`/treatments/${treatmentId}/referral-attachments`);

  const downloadTreatmentReferralAttachment = (treatmentId: TreatmentIdentifier, attachmentId: string) =>
    getRequest<Blob>(`/treatments/${treatmentId}/referral-attachments/${attachmentId}`, {
      responseType: "blob",
    });

  /** Fetches all available letter templates for treatments. */
  const getLetterTemplates = () => getRequest<Response<LetterTemplate[]>>(`/treatments/letter-templates`);

  /** Validates whether a letter template's requirements are met for a treatment. */
  const validateLetterTemplateRequirements = (treatmentId: TreatmentIdentifier, templateKey: string) =>
    getRequest<{ valid: boolean; message?: string; default_parameters?: Record<string, unknown> }>(
      `/treatments/${treatmentId}/documents/validate/${templateKey}`,
    );

  /** Creates a new document for a treatment. */
  const createTreatmentDocument = (treatmentId: TreatmentIdentifier, data: CreateTreatmentDocumentPayload) =>
    postRequest<Response<TreatmentDocument>>(`/treatments/${treatmentId}/documents`, data);

  /** Fetches a single treatment document by ID. */
  const getTreatmentDocument = (treatmentId: TreatmentIdentifier, documentId: TreatmentDocumentIdentifier) =>
    getRequest<Response<TreatmentDocument>>(`/treatments/${treatmentId}/documents/${documentId}`);

  /** Updates an existing treatment document. */
  const updateTreatmentDocument = (
    treatmentId: TreatmentIdentifier,
    documentId: TreatmentDocumentIdentifier,
    data: CreateTreatmentDocumentPayload,
  ) => putRequest<Response<TreatmentDocument>>(`/treatments/${treatmentId}/documents/${documentId}`, data);

  /** Downloads a treatment document as a PDF file. */
  const downloadTreatmentDocument = async (
    treatmentId: TreatmentIdentifier,
    documentId: TreatmentDocumentIdentifier,
  ) => {
    try {
      const response = await getRequest<BlobPart>(`/treatments/${treatmentId}/documents/${documentId}/download`, {
        responseType: "blob",
      });

      saveBlob(response, `treatment-document-${documentId}.pdf`, "application/pdf");
    } catch (error) {
      console.error("Error downloading treatment document:", error);
      throw error;
    }
  };

  /** Fetches a single checkpoint by ID. */
  const checkpoint = (checkpoint: CheckpointIdentifier) =>
    getRequest<Response<Checkpoint>>(`/checkpoints/${checkpoint}`);

  /** Fetches all checkpoints for a treatment. */
  const checkpoints = (treatmentId: TreatmentIdentifier) =>
    getRequest<CollectionResponse<Checkpoint>>(`/treatments/${treatmentId}/checkpoints`);

  /** Submits answers for a treatment checkpoint. */
  const submitCheckpoint = (checkpoint: CheckpointIdentifier, data: SubmitCheckpointPayload) =>
    putRequest<Response<Checkpoint>>(`/checkpoints/${checkpoint}`, data);

  return {
    startTreatment,
    updateTreatment,
    updateTreatmentReferral,
    recentTreatmentEpisodes,
    getTreatmentChangeFlow,
    changeTreatmentParameters,
    getTreatmentCreditRequest,
    updateBodyPartDiagnoses,
    getBodyPartDiagnoses,
    stopTreatment,
    deleteTreatment,
    treatment,
    treatments,
    storeTreatment,
    sendTreatment,
    treatmentStopReasons,
    referrerProviderTypes,
    treatmentJournals,
    fetchAllTreatmentJournals,
    treatmentAppointmentsOverview,
    treatmentJournal,
    treatmentJournalCopySource,
    addTreatmentJournal,
    saveTreatmentJournalDraft,
    treatmentDocuments,
    treatmentReferralAttachments,
    downloadTreatmentReferralAttachment,
    getLetterTemplates,
    validateLetterTemplateRequirements,
    createTreatmentDocument,
    getTreatmentDocument,
    updateTreatmentDocument,
    downloadTreatmentDocument,
    checkpoint,
    checkpoints,
    submitCheckpoint,
    // Aliases
    storeNewTreatment: storeTreatment,
    sendNewTreatment: sendTreatment,
  };
}

export type TreatmentApi = ReturnType<typeof createTreatmentApi>;
